import { useState } from 'react';
import axios from 'axios';
import { Clock3, IdCardIcon, Mail, MapPin, Phone, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import TurnstileWidget from '../components/TurnstileWidget';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const faqs = [
  {
    question: 'What areas do you service?',
    answer: 'We work across multiple cities and surrounding areas. If you are unsure whether your location is covered, send us a message and we will confirm availability for your area.',
  },
  {
    question: 'What is your response time for IT support?',
    answer: 'Urgent support requests are prioritised as quickly as possible, while standard enquiries are usually handled during business hours. Response time can vary depending on the issue, your location, and current workload.',
  },
  {
    question: 'Do you offer remote support?',
    answer: 'Yes. Many IT issues can be handled remotely, which often makes support faster and more convenient. If the job needs hands-on work, we can also arrange an on-site visit.',
  },
  {
    question: 'How do quotes and pricing work?',
    answer: 'Pricing depends on the type of service, the size of the job, and whether it is a one-off project or ongoing support. We can provide a quote once we understand what you need.',
  },
  {
    question: 'What types of services can I contact you about?',
    answer: 'You can reach out about networking, IT support, Starlink installation, web development, security systems, TV wall mounting, system administration, and cloud services.',
  },
  {
    question: 'Do you work with homes as well as businesses?',
    answer: 'Yes. We support both residential and business clients, and we tailor recommendations based on your setup, goals, and budget.',
  },
];

const contactInfo = [
  {
    icon: MapPin,
    title: 'Location',
    content: 'Multiple cities across Australia',
  },
  {
    icon: Phone,
    title: 'Phone',
    content: '+61 452 225 995',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'info@connect-ify.com.au',
  },
  {
    icon: IdCardIcon,
    title: 'Australian Business Number (ABN)',
    content: '30 672 391 657',
  },
];

const quickTrust = [
  {
    icon: Clock3,
    title: 'Fast replies',
    description: 'Straightforward communication and prompt follow-up on enquiries.',
  },
  {
    icon: ShieldCheck,
    title: 'Practical advice',
    description: 'Helpful guidance whether you need support, a quote, or the right next step.',
  },
];

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: '',
  });
  const [showErrors, setShowErrors] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const australianMobileRegex = /^(?:\+?61|0)4\d{8}$/;
    const australianLandlineRegex = /^(?:\+?61|0)[2378]\d{8}$/;

    return australianMobileRegex.test(cleanPhone) || australianLandlineRegex.test(cleanPhone);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    if (showErrors && errors.email) {
      setErrors({ ...errors, email: '' });
    }
  };

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setFormData({ ...formData, phone });

    if (showErrors && errors.phone) {
      setErrors({ ...errors, phone: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = { email: '', phone: '' };

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Please fill in all fields');
      setShowErrors(true);
      return;
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid Australian phone number (e.g., 04XX XXX XXX or +61 4XX XXX XXX)';
    }

    if (newErrors.email || newErrors.phone) {
      setErrors(newErrors);
      setShowErrors(true);
      toast.error('Please correct the errors in the form');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Please complete the captcha check');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        turnstileToken,
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTurnstileToken('');
      setTurnstileResetKey((current) => current + 1);
      setErrors({ email: '', phone: '' });
      setShowErrors(false);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        data-testid="contact-hero"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.8),transparent_45%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_35%)]" />
        </div>

        <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_0.95fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              Contact Connectify across multiple service regions
            </div>
            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-7xl dark:text-white">
              Let&apos;s talk about what
              <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                needs to get sorted.
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              Whether you need help with support, installations, networking, websites, or planning
              the right next step, send us a message and we&apos;ll point you in the right direction.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {quickTrust.map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5"
                >
                  <div className="flex items-start gap-4">
                    <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 p-3 text-white">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{item.title}</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-400/20 via-cyan-300/10 to-emerald-300/20 blur-2xl dark:from-sky-400/15 dark:to-emerald-400/10" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 p-8 text-white shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] dark:border-white/10">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-sky-200/80">Connectify</p>
                  <h2 className="mt-2 text-2xl font-bold">Business hours</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-sky-100">
                  Support-first
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="text-sm uppercase tracking-[0.22em] text-cyan-200">Weekdays</p>
                  <p className="mt-2 text-xl font-semibold">Monday - Friday: 9:00 AM - 5:00 PM</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <p className="text-sm uppercase tracking-[0.22em] text-cyan-200">Weekend</p>
                  <p className="mt-2 text-xl font-semibold">Saturday: 10:00 AM - 2:00 PM</p>
                  <p className="mt-1 text-sm text-slate-300">Sunday: Closed</p>
                </div>
                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5 text-sm text-emerald-100">
                  24/7 emergency support is available when urgent issues need fast attention.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="contact-form-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <div className="mb-8">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                  Send A Message
                </p>
                <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                  Tell us what you need help with.
                </h2>
              </div>

              <div className="rounded-[2rem] border border-slate-200/80 bg-white/80 p-8 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.35)] backdrop-blur dark:border-white/10 dark:bg-white/5">
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                      Your Name *
                    </label>
                    <Input
                      type="text"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                      className="border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={handleEmailChange}
                      placeholder="john@example.com"
                      required
                      className={`border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900 ${
                        showErrors && errors.email ? 'border-red-500 focus:ring-red-500' : ''
                      }`}
                    />
                    {showErrors && errors.email && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                      Phone Number *
                    </label>
                    <Input
                      type="tel"
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="+61 4XX XXX XXX or 04XX XXX XXX"
                      required
                      className={`border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900 ${
                        showErrors && errors.phone ? 'border-red-500 focus:ring-red-500' : ''
                      }`}
                    />
                    {showErrors && errors.phone && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                      Message *
                    </label>
                    <Textarea
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your IT needs..."
                      rows={6}
                      required
                      className="border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900"
                    />
                  </div>

                  {TURNSTILE_SITE_KEY && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                        Security Check *
                      </label>
                      <TurnstileWidget
                        siteKey={TURNSTILE_SITE_KEY}
                        onTokenChange={setTurnstileToken}
                        resetSignal={turnstileResetKey}
                      />
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        This helps protect the contact form from spam and automated abuse.
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="submit-contact-btn"
                    disabled={submitting}
                    className="w-full bg-[#1E90FF] text-white transition duration-300 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)]"
                  >
                    {submitting ? (
                      'Sending...'
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <span>Send Message</span>
                        <Send className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            <div>
              <div className="mb-8">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                  Contact Details
                </p>
                <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                  Reach out in the way that suits you best.
                </h2>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    data-testid={`contact-info-${index}`}
                    className="group rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-6 transition duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_65px_-28px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))] dark:hover:border-cyan-300/60 dark:hover:shadow-[0_24px_65px_-28px_rgba(34,211,238,0.35)]"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg">
                        <info.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-950 dark:text-white">
                          {info.title}
                        </h3>
                        <p className="mt-1 text-slate-600 dark:text-slate-300">{info.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-slate-950 py-20 text-white dark:border-white/10" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Frequently Asked Questions
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              A few quick answers before you get in touch.
            </h2>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] backdrop-blur">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  data-testid={`faq-item-${index}`}
                  className="border-white/10"
                >
                  <AccordionTrigger className="text-left text-white transition-colors hover:text-cyan-300">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
